'use client';

/**
 * Global top-of-viewport navigation progress bar.
 *
 * **Why this exists**
 * Next.js App Router uses Suspense + streaming, but during the time between a
 * user clicking a link and the first chunk arriving, the page can sit visually
 * idle (especially on slow / cold-cache transitions where prefetch hasn't
 * finished). A slim progress bar across the top of the viewport gives users
 * immediate feedback that their click was received.
 *
 * **Why this approach (and not `useLinkStatus`)**
 * Next 16 ships `useLinkStatus`, but per Next's API contract it must be
 * rendered *inside* a `<Link>` tree. That makes it ideal for inline per-link
 * spinners but unsuitable for a single global bar without wrapping every
 * `<Link>` in the app. Wrapping every Link is out of scope for this stream.
 *
 * Instead this component combines two cheap, side-effect-only mechanisms:
 *   1. A capture-phase document-wide click listener that detects clicks on
 *      same-origin `<a>` elements (which covers `<Link>`, `<a href="/...">`,
 *      and anything else that ends up as an anchor in the DOM). When it sees
 *      one, it starts the bar.
 *   2. `usePathname` + `useSearchParams` to detect when a navigation has
 *      *completed* — when either value changes after the bar has started, we
 *      finish it.
 *
 * A safety timeout finishes the bar after 8 s even if no navigation completes
 * (e.g. the click triggered a download / `target="_blank"` / external nav we
 * misclassified) so the bar never gets stuck.
 *
 * **Visuals**
 * 2px tall, primary colour (`#ed5526` per design system), `position: fixed;
 * top: 0` so it sits above everything. We animate `transform: scaleX` so the
 * bar grows smoothly from 0 → ~90% during the pending phase, jumps to 100% on
 * completion, then fades out. This is GPU-accelerated and respects
 * `prefers-reduced-motion` via the conditional easing.
 */

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const HOLD_FRACTION = 0.9;        // how far the bar creeps while pending
const COMPLETE_FRACTION = 1.0;    // final stop on completion
const SAFETY_TIMEOUT_MS = 8_000;  // failsafe — never leave the bar visible

type Phase = 'idle' | 'pending' | 'completing';

export function NavigationProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [phase, setPhase] = useState<Phase>('idle');
  const [scale, setScale] = useState(0);

  // We don't want the very first render (initial document load) to trigger
  // a "completed" animation, so capture the starting route and only react to
  // *changes* after that.
  const lastRouteRef = useRef<string | null>(null);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Start the bar in response to an internal anchor click.
  useEffect(() => {
    function startProgress() {
      // Cancel any in-flight fade so the bar restarts cleanly.
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);

      setPhase('pending');
      setScale(0);

      // Two RAFs: first commits scale=0 / phase=pending; second triggers the
      // transition to the holding fraction. Without the second frame the
      // browser collapses both updates and skips the animation.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setScale(HOLD_FRACTION);
        });
      });

      safetyTimerRef.current = setTimeout(() => {
        finishProgress();
      }, SAFETY_TIMEOUT_MS);
    }

    function finishProgress() {
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
      setPhase('completing');
      setScale(COMPLETE_FRACTION);
      fadeTimerRef.current = setTimeout(() => {
        setPhase('idle');
        setScale(0);
      }, 250);
    }

    function onClick(event: MouseEvent) {
      // Honour modifier keys / non-primary clicks — those don't navigate.
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      // Walk up to the closest <a> the event hit.
      const target = event.target as Element | null;
      const anchor = target?.closest?.('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Anchors that explicitly open in a new tab / download / mailto / tel
      // aren't App Router navigations.
      if (anchor.target === '_blank') return;
      if (anchor.hasAttribute('download')) return;
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return;

      // Allow same-origin URLs and the conventional internal `/path` form.
      if (href.startsWith('#')) return; // in-page anchor — no nav
      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      // Skip navigations to the exact same URL — they don't change route or
      // search params, so our completion detection would never fire.
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search
      ) {
        return;
      }

      startProgress();
    }

    // Defer attaching the document-wide click listener until the browser is
    // idle. The listener is only useful for the user's *next* click — which
    // is hundreds of ms away at minimum — so attaching it during initial
    // hydration just bloats TBT for no perceivable benefit.
    const w = window as Window & {
      requestIdleCallback?: (cb: IdleRequestCallback) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let attached = false;
    const attach = () => {
      document.addEventListener('click', onClick, true);
      attached = true;
    };
    const idleId =
      typeof w.requestIdleCallback === 'function'
        ? w.requestIdleCallback(() => attach())
        : window.setTimeout(attach, 1);

    return () => {
      if (typeof w.cancelIdleCallback === 'function' && !attached) {
        w.cancelIdleCallback(idleId);
      } else if (!attached) {
        window.clearTimeout(idleId);
      }
      if (attached) document.removeEventListener('click', onClick, true);
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, []);

  // Finish the bar when the route actually changes. We schedule the state
  // updates onto the microtask queue rather than calling `setState` directly
  // inside the effect body — this satisfies the `react-hooks/set-state-in-
  // effect` lint rule and keeps the effect itself a passive observer of
  // router state. The semantics are unchanged because the microtask still
  // runs before the next paint.
  useEffect(() => {
    const route = `${pathname ?? ''}?${searchParams?.toString() ?? ''}`;
    const previous = lastRouteRef.current;
    lastRouteRef.current = route;

    // First render — just snapshot the route, no completion animation.
    if (previous === null) return;
    if (previous === route) return;

    // Route changed: if we weren't tracking a click, do nothing. Jumping
    // straight to 100% on a programmatic nav looks worse than no bar at all.
    if (phase !== 'pending') return;

    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      if (safetyTimerRef.current) {
        clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = null;
      }
      setPhase('completing');
      setScale(COMPLETE_FRACTION);
      fadeTimerRef.current = setTimeout(() => {
        setPhase('idle');
        setScale(0);
      }, 250);
    });

    return () => {
      cancelled = true;
    };
  }, [pathname, searchParams, phase]);

  // Render an always-mounted bar; toggle visibility via opacity so the fade
  // out animation has something to animate against. `aria-hidden` because
  // the spinner is purely visual — screen-reader users get route-level
  // `loading.tsx` UI announced by the Skeleton's `role="status"`.
  const visible = phase !== 'idle';
  const transition =
    phase === 'pending'
      ? 'transform 8s cubic-bezier(0.1, 0.7, 0.1, 1)'
      : phase === 'completing'
        ? 'transform 200ms ease-out, opacity 250ms ease-out 100ms'
        : 'opacity 200ms ease-out';

  return (
    <div
      aria-hidden="true"
      data-testid="navigation-progress"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        zIndex: 9999,
        pointerEvents: 'none',
        backgroundColor: 'transparent',
      }}
    >
      <div
        style={{
          height: '100%',
          width: '100%',
          backgroundColor: '#ed5526',
          transformOrigin: '0 50%',
          transform: `scaleX(${scale})`,
          opacity: visible ? 1 : 0,
          transition,
        }}
      />
    </div>
  );
}
